import { useState, useCallback, useMemo, useEffect } from 'react';

type UseRowSelectionParams = {
  pageItemIds: string[];
  page: number;
  pageSize: number;
};

export function useRowSelection({
  pageItemIds,
  page,
  pageSize,
}: UseRowSelectionParams) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, pageSize]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = pageItemIds.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(pageItemIds);
    });
  }, [pageItemIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isAllSelected = useMemo(
    () =>
      pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.has(id)),
    [pageItemIds, selectedIds],
  );

  const isPartiallySelected = useMemo(
    () =>
      pageItemIds.some((id) => selectedIds.has(id)) &&
      !pageItemIds.every((id) => selectedIds.has(id)),
    [pageItemIds, selectedIds],
  );

  return {
    selectedIds,
    toggleRow,
    toggleAll,
    clearSelection,
    isAllSelected,
    isPartiallySelected,
    selectedCount: selectedIds.size,
  };
}
